export type RecommendationCacheType = 'offensive' | 'defensive';

export interface RecommendationCacheEventDetail {
  type: RecommendationCacheType;
  key: string;
  data: any;
}

export const RECOMMENDATIONS_CACHE_EVENT = 'recommendations:cache-update';

const offensiveCache = new Map<string, any>();
const defensiveCache = new Map<string, any>();

const getCacheMap = (type: RecommendationCacheType) => {
  return type === 'defensive' ? defensiveCache : offensiveCache;
};

const inFlightOffensive = new Set<string>();
const inFlightDefensive = new Set<string>();

const getInFlightSet = (type: RecommendationCacheType) => {
  return type === 'defensive' ? inFlightDefensive : inFlightOffensive;
};

export const buildRecommendationCacheKey = (
  type: RecommendationCacheType,
  opponentTeamId: string,
  ourTeamId: string,
  seasonId?: string,
  stage?: string
) => {
  const stageKey = stage || 'regular_season';
  if (seasonId) {
    return `${type}_recommendations_${opponentTeamId}_${ourTeamId}_${seasonId}_${stageKey}`;
  }
  return `${type}_recommendations_${opponentTeamId}_${ourTeamId}_${stageKey}`;
};

export const getRecommendationCache = (
  type: RecommendationCacheType,
  opponentTeamId: string,
  ourTeamId: string,
  seasonId?: string,
  stage?: string
) => {
  const key = buildRecommendationCacheKey(type, opponentTeamId, ourTeamId, seasonId, stage);
  return getCacheMap(type).get(key);
};

export const getRecommendationCacheSnapshot = (type: RecommendationCacheType) => {
  return new Map(getCacheMap(type));
};

export const markRecommendationRequestInFlight = (type: RecommendationCacheType, key: string) => {
  getInFlightSet(type).add(key);
};

export const clearRecommendationRequestInFlight = (type: RecommendationCacheType, key: string) => {
  getInFlightSet(type).delete(key);
};

export const isRecommendationRequestInFlight = (type: RecommendationCacheType, key: string) => {
  return getInFlightSet(type).has(key);
};

export const setRecommendationCache = (
  type: RecommendationCacheType,
  opponentTeamId: string,
  ourTeamId: string,
  data: any,
  seasonId?: string,
  stage?: string
) => {
  const key = buildRecommendationCacheKey(type, opponentTeamId, ourTeamId, seasonId, stage);
  getCacheMap(type).set(key, data);

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<RecommendationCacheEventDetail>(RECOMMENDATIONS_CACHE_EVENT, {
        detail: {
          type,
          key,
          data
        }
      })
    );
  }

  return key;
};

export const clearRecommendationCaches = () => {
  offensiveCache.clear();
  defensiveCache.clear();
  inFlightOffensive.clear();
  inFlightDefensive.clear();
};
